import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { useQuery } from '../../../hooks/useQuery'
import { Dispatch, SetStateAction, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { PRESS_RELEASES_FILTER_CONTENT_TYPE, PressReleasesFilterContentType } from '@finance-lab/utils/types'

export type PressReleasesPaginationModel = {
    page: number
    pageSize: number
}

export const PRESS_RELAESES_FILTER_PARAMS = {
    SEARCH: 'search',
    CONTENT_TYPE: 'contentType',
    END_DATE: 'endDate',
} as const

export const SELECT_PRESS_RELEASE_PARAM = 'selectPressRelease'

export const validateContentType = (value: string | null): PressReleasesFilterContentType => {
    const validTypes = Object.values(PRESS_RELEASES_FILTER_CONTENT_TYPE) as PressReleasesFilterContentType[]
    return validTypes.find((k) => k === value) ?? 'all'
}

export const validateEndDate = (value: string | null): string => {
    if (!value || isNaN(new Date(value).getTime())) {
        return formatDateYYYYMMDD(new Date(), true)
    }

    return value
}

export const useFilterSettings = () => {
    const query = useQuery()

    const result = useMemo(
        () => ({
            endDate: validateEndDate(query.get(PRESS_RELAESES_FILTER_PARAMS.END_DATE)),
            contentType: validateContentType(query.get(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE)),
            searchValue: query.get(PRESS_RELAESES_FILTER_PARAMS.SEARCH) ?? undefined,
        }),
        [query],
    )

    return result
}

export const usePressReleaseSelectDialog = () => {
    const query = useQuery()
    const navigate = useNavigate()

    const isOpen = query.get(SELECT_PRESS_RELEASE_PARAM) === '1'

    const open = useCallback(() => {
        const params = new URLSearchParams(query)
        params.set(SELECT_PRESS_RELEASE_PARAM, '1')
        params.set(PRESS_RELAESES_FILTER_PARAMS.END_DATE, formatDateYYYYMMDD(new Date(), true))
        params.delete(PRESS_RELAESES_FILTER_PARAMS.SEARCH)
        params.delete(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE)
        navigate(`?${params.toString()}`, { replace: true })
    }, [navigate, query])

    const close = useCallback(() => {
        const params = new URLSearchParams(query)
        params.delete(SELECT_PRESS_RELEASE_PARAM)
        navigate(`?${params.toString()}`, { replace: true })
    }, [navigate, query])

    return { isOpen, open, close }
}

/** Reset to first page when filter changes – avoids empty lists after search on a later page. */
export const useResetPaginationOnFilterChange = (
    setPaginationModel: Dispatch<SetStateAction<PressReleasesPaginationModel>>,
    { endDate, searchValue, contentType }: ReturnType<typeof useFilterSettings>,
) => {
    const hasMounted = useRef(false)
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true
            return
        }

        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
    }, [contentType, endDate, searchValue, setPaginationModel])
}
